/* global crypto, TextEncoder, URL */

const DISCOVERY_DOMAIN = "novatime-connector-discovery-v1";
const NUL = String.fromCharCode(0);
const BACKSLASH = String.fromCharCode(92);
const DOUBLE_BACKSLASH = BACKSLASH.repeat(2);
const GUID_PATTERN = /^[0-9a-f]{32}$/i;

function normalizeGuid(value) {
  const compact = String(value || "")
    .trim()
    .replace(/^guid:/i, "")
    .replace(/[{}()_-]/g, "")
    .replace(/-/g, "");
  if (!GUID_PATTERN.test(compact) || /^0+$/.test(compact)) return "";
  const lower = compact.toLowerCase();
  return `guid:${lower.slice(0, 8)}-${lower.slice(8, 12)}-${lower.slice(12, 16)}-${lower.slice(16, 20)}-${lower.slice(20)}`;
}

function decodeUrlPart(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function collapseSegments(segments, protectedCount = 0) {
  const output = [];
  segments.forEach((segment) => {
    if (!segment || segment === ".") return;
    if (segment === "..") {
      if (output.length > protectedCount) output.pop();
      return;
    }
    output.push(segment);
  });
  return output;
}

function trimTrailingSeparators(value) {
  let result = value;
  while (result.endsWith(BACKSLASH)) result = result.slice(0, -1);
  return result;
}

function normalizeWindowsPath(value) {
  let path = String(value || "")
    .trim()
    .normalize("NFC");
  if (!path) return "";
  if (/^file:/i.test(path)) {
    let parsed;
    try {
      parsed = new URL(path);
    } catch {
      return "";
    }
    let decodedPath = decodeUrlPart(parsed.pathname).replace(/\//g, BACKSLASH);
    if (
      !parsed.hostname &&
      decodedPath.startsWith(BACKSLASH) &&
      /^[a-z]:/i.test(decodedPath.slice(1))
    ) {
      decodedPath = decodedPath.slice(1);
    }
    path = parsed.hostname ? `${DOUBLE_BACKSLASH}${parsed.hostname}${decodedPath}` : decodedPath;
  }
  path = path.replace(/\//g, BACKSLASH);
  if (/^[a-z]:/i.test(path) && path.charAt(2) === BACKSLASH) {
    const drive = path.slice(0, 2).toLowerCase();
    const segments = collapseSegments(path.slice(3).split(BACKSLASH));
    return trimTrailingSeparators(
      `path:${drive}${BACKSLASH}${segments.join(BACKSLASH)}`.toLowerCase()
    );
  }
  if (path.startsWith(DOUBLE_BACKSLASH)) {
    const segments = collapseSegments(path.slice(2).split(BACKSLASH), 2);
    if (segments.length < 2) return "";
    return trimTrailingSeparators(
      `path:${DOUBLE_BACKSLASH}${segments.join(BACKSLASH)}`.toLowerCase()
    );
  }
  return "";
}

function normalizeHttpUrl(value) {
  let parsed;
  try {
    parsed = new URL(value);
  } catch {
    return "";
  }
  const protocol = parsed.protocol.toLowerCase();
  if (protocol !== "http:" && protocol !== "https:") return "";
  const defaultPort =
    (protocol === "http:" && parsed.port === "80") ||
    (protocol === "https:" && parsed.port === "443");
  const authority =
    parsed.hostname.toLowerCase() + (parsed.port && !defaultPort ? `:${parsed.port}` : "");
  const decodedPath = decodeUrlPart(parsed.pathname).normalize("NFC");
  const segments = collapseSegments(decodedPath.split("/"));
  return `url:${protocol}//${authority}/${segments.join("/")}${parsed.search}`;
}

export function canonicalizeProjectIdentity(stableIdentity) {
  const raw = String(stableIdentity || "")
    .trim()
    .normalize("NFC");
  if (!raw || /^(unsaved|sidecar-name):/i.test(raw)) return "";
  const guid = normalizeGuid(raw);
  if (guid) return guid;
  if (/^path:/i.test(raw)) return normalizeWindowsPath(raw.slice(5));
  if (/^url:/i.test(raw)) {
    const value = raw.slice(4);
    return /^file:/i.test(value) || /^[a-z]:/i.test(value) || value.startsWith(DOUBLE_BACKSLASH)
      ? normalizeWindowsPath(value)
      : normalizeHttpUrl(value);
  }
  if (/^file:/i.test(raw) || /^[a-z]:/i.test(raw) || raw.startsWith(DOUBLE_BACKSLASH)) {
    return normalizeWindowsPath(raw);
  }
  if (/^https?:/i.test(raw)) return normalizeHttpUrl(raw);
  return "";
}

async function sha256Hex(value) {
  const bytes =
    typeof TextEncoder !== "undefined" ? new TextEncoder().encode(value) : encodeUtf8(value);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function encodeUtf8(value) {
  const bytes = [];
  for (let index = 0; index < value.length; index += 1) {
    let codePoint = value.charCodeAt(index);
    if (codePoint >= 0xd800 && codePoint <= 0xdbff) {
      const low = value.charCodeAt(index + 1);
      if (low >= 0xdc00 && low <= 0xdfff) {
        codePoint = 0x10000 + ((codePoint - 0xd800) << 10) + (low - 0xdc00);
        index += 1;
      } else {
        codePoint = 0xfffd;
      }
    } else if (codePoint >= 0xdc00 && codePoint <= 0xdfff) {
      codePoint = 0xfffd;
    }

    if (codePoint <= 0x7f) {
      bytes.push(codePoint);
    } else if (codePoint <= 0x7ff) {
      bytes.push(0xc0 | (codePoint >> 6));
      bytes.push(0x80 | (codePoint & 0x3f));
    } else if (codePoint <= 0xffff) {
      bytes.push(0xe0 | (codePoint >> 12));
      bytes.push(0x80 | ((codePoint >> 6) & 0x3f));
      bytes.push(0x80 | (codePoint & 0x3f));
    } else {
      bytes.push(0xf0 | (codePoint >> 18));
      bytes.push(0x80 | ((codePoint >> 12) & 0x3f));
      bytes.push(0x80 | ((codePoint >> 6) & 0x3f));
      bytes.push(0x80 | (codePoint & 0x3f));
    }
  }
  return new Uint8Array(bytes);
}

export async function createProjectBindingHashes(stableIdentities, environmentId, maxBindings = 3) {
  const values = Array.isArray(stableIdentities) ? stableIdentities : [stableIdentities];
  const canonical = [];
  values.forEach((value) => {
    const identity = canonicalizeProjectIdentity(value);
    if (identity && !canonical.includes(identity) && canonical.length < maxBindings) {
      canonical.push(identity);
    }
  });
  const environment = String(environmentId || "").trim();
  if (!environment || canonical.length === 0) return [];
  return Promise.all(
    canonical.map((identity) =>
      sha256Hex(`${DISCOVERY_DOMAIN}${NUL}${environment}${NUL}${identity}`)
    )
  );
}
